"use client";

import { useRouter } from "next/navigation";
import React, { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import styles from "./Navbar.module.css";
import { MenuItem } from "@/models/menuItem";
import ScheduleIcon from "../../../../public/images/navbar/schedule.svg";
import LineUpIcon from "../../../../public/images/navbar/lineup.svg";
import ProfileIcon from "../../../../public/images/profileNavbar/profile.svg";
import CreatorAccountIcon from "../../../../public/images/profileNavbar/creator_account.svg";
import EventCheckInIcon from "../../../../public/images/navbar/event-check-in.svg";
import FormsIcon from "../../../../public/icons/checklist-forms.svg";
import PeopleIcon from "@mui/icons-material/People";
import LiveStreamIcon from "../../../../public/icons/multimedia-button-circle.svg";
import GetInvolvedIcon from "../../../../public/icons/group.svg";
import StripeCouponsIcon from "../../../../public/icons/stripe-coupons.svg";
import PartnerPortalIcon from "../../../../public/icons/admin-partner-portal.svg";
import PassIcon from "../../../../public/images/navbar/passes.svg";
import DashboardIcon from "../../../../public/images/navbar/dashboard.svg";

import { signOut } from "@/helpers/firebase/auth";
import { useDispatch } from "react-redux";
import { logOut } from "@/redux/features/app-slice";
import { resetState as resetMarketPlaceState } from "@/redux/features/market-place-slice";
import { useAppSelector } from "@/redux/store";
import { useGetWorkspacesByUserQuery } from "@/redux/features/kanban-api";
import { Workspace, Space, List } from "@/models/kanban";
import { UserRoles } from "@/models/user";

// MUI Icons
import FolderIcon from "@mui/icons-material/Folder";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AssessmentIcon from "@mui/icons-material/Assessment";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

interface NavCategory {
  name: string;
  items: MenuItem[];
  isOpen?: boolean;
}

const initialCategories: NavCategory[] = [
  {
    name: "Dashboard",
    items: [
      {
        name: "Dashboard",
        href: "/admin/dashboard",
        IconComponent: DashboardIcon,
      },
    ],
  },
  {
    name: "Project Management",
    items: [
      {
        name: "Workspaces",
        href: "/admin/workspaces",
        IconComponent: DashboardIcon,
      },
      {
        name: "Goals & OKRs",
        href: "/admin/workspaces/goals",
        IconComponent: DashboardIcon,
      },
      {
        name: "Check-ins",
        href: "/admin/workspaces/checkins",
        IconComponent: DashboardIcon,
      },
      {
        name: "Dashboard",
        href: "/admin/workspaces/dashboard",
        IconComponent: DashboardIcon,
      },
    ],
  },
  {
    name: "Event Content",
    items: [
      {
        name: "Schedule",
        href: "/admin/schedule",
        IconComponent: ScheduleIcon,
      },
      {
        name: "Lineup",
        href: "/admin/lineup",
        IconComponent: LineUpIcon,
      },
      {
        name: "Venue",
        href: "/admin/venue",
        IconComponent: LineUpIcon,
      },
      {
        name: "Announcements",
        href: "/admin/announcement",
        IconComponent: ProfileIcon,
      },
    ],
  },
  {
    name: "Media",
    items: [
      {
        name: "Videos",
        href: "/admin/video",
        IconComponent: CreatorAccountIcon,
      },
      {
        name: "Live Stream",
        href: "/admin/stream",
        IconComponent: LiveStreamIcon,
      },
    ],
  },
  {
    name: "Operations",
    items: [
      {
        name: "Event Check-In",
        href: "/admin/event-check-in",
        IconComponent: EventCheckInIcon,
      },
      {
        name: "QR Printer",
        href: "/admin/qr-printer",
        IconComponent: EventCheckInIcon,
      },
    ],
  },
  {
    name: "Engagement",
    items: [
      {
        name: "Participant CRM",
        href: "/admin/crm",
        IconComponent: PeopleIcon as any,
      },
      {
        name: "Applications",
        href: "/admin/submissions",
        IconComponent: FormsIcon,
      },
      {
        name: "Forms",
        href: "/admin/forms",
        IconComponent: FormsIcon,
      },
      {
        name: "Get Involved",
        href: "/admin/get-involved-controls",
        IconComponent: GetInvolvedIcon,
      },
    ],
  },
  {
    name: "Partnerships",
    items: [
      {
        name: "Partner Portal",
        href: "/admin/partner-portal",
        IconComponent: PartnerPortalIcon,
      },
      {
        name: "Documents",
        href: "/admin/documents",
        IconComponent: FormsIcon,
      },
    ],
  },
  {
    name: "Ticketing",
    items: [
      {
        name: "Allowed Events For Passes",
        href: "/admin/allowed-events",
        IconComponent: PassIcon,
      },
      {
        name: "Stripe Coupons",
        href: "/admin/stripe-coupons",
        IconComponent: StripeCouponsIcon,
      },
      {
        name: "Email Templates",
        href: "/admin/email-templates",
        IconComponent: FormsIcon,
      },
    ],
  },
  {
    name: "News",
    items: [
      {
        name: "Content Studio",
        href: "https://cms.ctribesociety.com",
        IconComponent: FormsIcon,
      },
      {
        name: "Articles",
        href: "/admin/news/articles",
        IconComponent: FormsIcon,
      },
      {
        name: "Media Library",
        href: "/admin/news/media",
        IconComponent: CreatorAccountIcon,
      },
    ],
  },
];

export default function AdminBar({ show, setter }: any) {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useDispatch();
  const [categories, setCategories] = useState<NavCategory[]>(
    initialCategories.map((cat) => ({ ...cat, isOpen: true }))
  );

  // Get user data for workspaces
  const userData = useAppSelector((state) => state.appReducer.userData);
  const isAdminUser = userData?.role === UserRoles.admin;
  const userId = userData?.uid || "";

  // Fetch user's workspaces
  const { data: workspaces = [] } = useGetWorkspacesByUserQuery(userId, {
    skip: !userId,
  });

  // Track expanded workspaces in sidebar
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Record<string, boolean>>({});

  // Get spaces and lists from Redux (set by workspace layout)
  const spaces = useAppSelector((state) => state.kanbanReducer.spaces);
  const lists = useAppSelector((state) => state.kanbanReducer.lists);

  const handleMenuItemClick = async (menuItem: MenuItem) => {
    if (menuItem.href) {
      if (menuItem.href.startsWith("http://") || menuItem.href.startsWith("https://")) {
        window.open(menuItem.href, "_blank", "noopener,noreferrer");
      } else {
        router.push(menuItem.href);
      }
    } else if (menuItem.fn) {
      menuItem.fn();
    }
    setter(false);
  };

  const toggleCategory = (categoryName: string) => {
    setCategories((prevCategories) =>
      prevCategories.map((cat) =>
        cat.name === categoryName ? { ...cat, isOpen: !cat.isOpen } : cat
      )
    );
  };

  const toggleWorkspace = (workspaceId: string) => {
    setExpandedWorkspaces((prev) => ({
      ...prev,
      [workspaceId]: !prev[workspaceId],
    }));
  };

  const doLogOut = () => {
    router.push("/");
    signOut();
    dispatch(logOut());
    dispatch(resetMarketPlaceState());
  };

  const isItemSelected = (href?: string) => {
    return !!href && pathname.includes(href);
  };

  const isCategoryActive = (category: NavCategory) => {
    return category.items.some((item) => isItemSelected(item.href));
  };

  // Check if we're in a workspace context
  const isInWorkspace = pathname.startsWith("/admin/workspaces/") && pathname.split("/").length > 3;
  const currentWorkspaceSlug = isInWorkspace ? pathname.split("/")[3] : null;

  return (
    <>
      <div
        className={`h-screen pt-7 pl-[1.875rem] flex-col flex bg-white bg-opacity-30 justify-between`}
      >
        <div className="overflow-y-auto flex-1 pr-4">
          <div className="flex justify-between items-center">
            <Image
              height={34}
              width={80}
              className="mt-0"
              src="/images/C-Tribe_Main_Logo.svg"
              alt="c-tribe"
            />
          </div>

          <div
            className="flex flex-col cursor-pointer "
            onClick={() => {
              router.push("/profile");
            }}
          >
            <div className={`flex justify-start items-center`}>
              <div className={`flex items-center justify-start mt-6`}>
                <Image
                  src={"/images/profileNavbar/home_back.svg"}
                  width={20}
                  height={20}
                  alt="sideBar"
                  color="inherit"
                  style={{ objectFit: "contain" }}
                  quality={75}
                />
                <span className={`ml-3 text-sm font-medium`}>
                  Account Settings
                </span>
              </div>
            </div>
          </div>

          <div
            className="flex cursor-pointer items-center justify-start mt-4"
            onClick={() => router.push("/admin/announcement")}
          >
            <span className={`text-sm font-medium`}>Admin Panel</span>
          </div>

          <div className="flex flex-col gap-2 mt-4">
            {categories.map((category, categoryIndex) => {
              const isActive = isCategoryActive(category);
              const isDashboard = category.name === "Dashboard";
              const isProjectManagement = category.name === "Project Management";

              // Dashboard is a single item, render directly
              if (isDashboard) {
                const item = category.items[0];
                const IconComponent = item.IconComponent;
                const isSelected = isItemSelected(item.href);
                const iconColor = isSelected ? "#333E48" : "#909090";
                return (
                  <div
                    key={categoryIndex}
                    className={`cursor-pointer flex justify-start items-center py-2`}
                    onClick={() => handleMenuItemClick(item)}
                  >
                    <div className={`flex items-center justify-start`}>
                      {IconComponent && (
                        <IconComponent
                          width={20}
                          height={20}
                          style={{ color: iconColor }}
                        />
                      )}
                      <span
                        className={`ml-3 text-sm ${
                          isSelected
                            ? "font-bold text-black"
                            : "font-normal text-custom-gray2"
                        } ${isSelected ? "" : styles.unselectedMenuItem}`}
                      >
                        {item.name}
                      </span>
                    </div>
                  </div>
                );
              }

              // Project Management has special rendering with workspaces
              if (isProjectManagement) {
                return (
                  <div key={categoryIndex} className="mt-2">
                    {/* Category Header */}
                    <div
                      className={`flex items-center justify-between cursor-pointer py-1 ${styles.categoryHeader}`}
                      onClick={() => toggleCategory(category.name)}
                    >
                      <span
                        className={`text-xs font-semibold uppercase tracking-wider ${
                          isActive || pathname.startsWith("/admin/workspaces")
                            ? "text-gray-700"
                            : "text-gray-500"
                        }`}
                      >
                        {category.name}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                          category.isOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>

                    {/* Category Items */}
                    <div
                      className={`overflow-hidden transition-all duration-200 ${
                        category.isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="flex flex-col gap-1 mt-2 ml-2">
                        {/* Static menu items */}
                        <Link
                          href="/admin/workspaces"
                          className={`flex items-center gap-2 py-1.5 text-sm ${
                            pathname === "/admin/workspaces"
                              ? "font-bold text-black"
                              : "font-normal text-custom-gray2 hover:text-black"
                          }`}
                        >
                          <WorkspacesIcon style={{ fontSize: 16 }} />
                          <span>Workspaces Overview</span>
                        </Link>

                        <Link
                          href="/admin/workspaces/goals"
                          className={`flex items-center gap-2 py-1.5 text-sm ${
                            pathname === "/admin/workspaces/goals"
                              ? "font-bold text-black"
                              : "font-normal text-custom-gray2 hover:text-black"
                          }`}
                        >
                          <TrackChangesIcon style={{ fontSize: 16 }} />
                          <span>Goals & OKRs</span>
                        </Link>

                        <Link
                          href="/admin/workspaces/checkins"
                          className={`flex items-center gap-2 py-1.5 text-sm ${
                            pathname === "/admin/workspaces/checkins"
                              ? "font-bold text-black"
                              : "font-normal text-custom-gray2 hover:text-black"
                          }`}
                        >
                          <EventNoteIcon style={{ fontSize: 16 }} />
                          <span>Check-ins</span>
                        </Link>

                        <Link
                          href="/admin/workspaces/dashboard"
                          className={`flex items-center gap-2 py-1.5 text-sm ${
                            pathname === "/admin/workspaces/dashboard"
                              ? "font-bold text-black"
                              : "font-normal text-custom-gray2 hover:text-black"
                          }`}
                        >
                          <AssessmentIcon style={{ fontSize: 16 }} />
                          <span>Progress Dashboard</span>
                        </Link>

                        {/* Divider */}
                        {workspaces.length > 0 && (
                          <div className="border-t border-gray-200 my-2" />
                        )}

                        {/* Dynamic Workspaces */}
                        {workspaces.map((workspace) => {
                          const isExpanded = expandedWorkspaces[workspace.id];
                          const isCurrentWorkspace = currentWorkspaceSlug === workspace.slug;
                          const workspaceSpaces = isCurrentWorkspace ? spaces : [];
                          const workspaceLists = isCurrentWorkspace ? lists : [];

                          return (
                            <div key={workspace.id} className="group/workspace">
                              <div
                                className={`flex items-center gap-2 py-1.5 cursor-pointer text-sm ${
                                  isCurrentWorkspace
                                    ? "font-bold text-black"
                                    : "font-normal text-custom-gray2 hover:text-black"
                                }`}
                                onClick={() => {
                                  if (!isExpanded) {
                                    router.push(`/admin/workspaces/${workspace.slug}`);
                                  }
                                  toggleWorkspace(workspace.id);
                                }}
                              >
                                <button className="p-0.5 hover:bg-gray-100 rounded">
                                  {isExpanded ? (
                                    <ExpandMoreIcon style={{ fontSize: 14 }} />
                                  ) : (
                                    <ChevronRightIcon style={{ fontSize: 14 }} />
                                  )}
                                </button>
                                <div
                                  className="w-4 h-4 rounded flex items-center justify-center text-white text-[10px] font-bold"
                                  style={{ backgroundColor: workspace.color || "#3b82f6" }}
                                >
                                  {workspace.name.charAt(0)}
                                </div>
                                <span className="truncate flex-1">{workspace.name}</span>
                                {/* Settings menu - appears on hover */}
                                <Link
                                  href={`/admin/workspaces/${workspace.slug}/settings`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1 rounded hover:bg-gray-100 opacity-0 group-hover/workspace:opacity-100 transition-opacity"
                                  title="Workspace settings"
                                  aria-label={`Settings for ${workspace.name}`}
                                >
                                  <MoreHorizIcon style={{ fontSize: 14 }} className="text-gray-400" />
                                </Link>
                              </div>

                              {/* Expanded workspace content */}
                              {isExpanded && isCurrentWorkspace && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {/* Quick Links */}
                                  <Link
                                    href={`/admin/workspaces/${workspace.slug}`}
                                    className={`flex items-center gap-2 py-1 text-xs ${
                                      pathname === `/admin/workspaces/${workspace.slug}`
                                        ? "font-semibold text-black"
                                        : "text-gray-500 hover:text-black"
                                    }`}
                                  >
                                    <AssessmentIcon style={{ fontSize: 12 }} />
                                    <span>Overview</span>
                                  </Link>
                                  <Link
                                    href={`/admin/workspaces/${workspace.slug}/my-tasks`}
                                    className={`flex items-center gap-2 py-1 text-xs ${
                                      pathname.includes("/my-tasks")
                                        ? "font-semibold text-black"
                                        : "text-gray-500 hover:text-black"
                                    }`}
                                  >
                                    <AssignmentIcon style={{ fontSize: 12 }} />
                                    <span>My Tasks</span>
                                  </Link>

                                  {/* Divider before Spaces */}
                                  {workspaceSpaces.length > 0 && (
                                    <div className="border-t border-gray-100 my-1.5" />
                                  )}

                                  {/* Spaces */}
                                  {workspaceSpaces.map((space) => {
                                    const spaceLists = workspaceLists.filter(
                                      (l) => l.spaceId === space.id
                                    );

                                    return (
                                      <div key={space.id} className="mt-1">
                                        <div className="flex items-center gap-1.5 py-0.5 text-xs text-gray-500">
                                          <div
                                            className="w-3 h-3 rounded flex items-center justify-center"
                                            style={{ backgroundColor: space.color || "#6366f1" }}
                                          >
                                            <FolderIcon style={{ fontSize: 8, color: "white" }} />
                                          </div>
                                          <span className="font-medium truncate">{space.name}</span>
                                        </div>
                                        {spaceLists.map((list) => (
                                          <Link
                                            key={list.id}
                                            href={`/admin/workspaces/${workspace.slug}/${space.slug}/${list.slug}`}
                                            className={`flex items-center gap-1.5 py-0.5 ml-3 text-xs ${
                                              pathname.includes(`/${space.slug}/${list.slug}`)
                                                ? "font-semibold text-black"
                                                : "text-gray-400 hover:text-black"
                                            }`}
                                          >
                                            <ListAltIcon style={{ fontSize: 10 }} />
                                            <span className="truncate">{list.name}</span>
                                          </Link>
                                        ))}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={categoryIndex} className="mt-2">
                  {/* Category Header */}
                  <div
                    className={`flex items-center justify-between cursor-pointer py-1 ${styles.categoryHeader}`}
                    onClick={() => toggleCategory(category.name)}
                  >
                    <span
                      className={`text-xs font-semibold uppercase tracking-wider ${
                        isActive ? "text-gray-700" : "text-gray-500"
                      }`}
                    >
                      {category.name}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                        category.isOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>

                  {/* Category Items */}
                  <div
                    className={`overflow-hidden transition-all duration-200 ${
                      category.isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="flex flex-col gap-3 mt-2 ml-2">
                      {category.items.map((item, index) => {
                        if (item.name === "Content Studio" && !isAdminUser) {
                          return null;
                        }
                        const IconComponent = item.IconComponent;
                        const isSelected = isItemSelected(item.href);
                        const iconColor = isSelected ? "#333E48" : "#909090";
                        return (
                          <div
                            key={index}
                            className={`cursor-pointer flex justify-start items-center`}
                            onClick={() => handleMenuItemClick(item)}
                          >
                            <div className={`flex items-center justify-start`}>
                              {IconComponent && (
                                <IconComponent
                                  width={18}
                                  height={18}
                                  style={{ color: iconColor }}
                                />
                              )}
                              <span
                                className={`ml-3 text-sm ${
                                  isSelected
                                    ? "font-bold text-black"
                                    : "font-normal text-custom-gray2"
                                } ${isSelected ? "" : styles.unselectedMenuItem}`}
                              >
                                {item.name}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div
          className="flex flex-col cursor-pointer md:mt-auto mt-5 md:pb-10 pb-20"
          onClick={() => {
            doLogOut();
          }}
        >
          <div className={`flex justify-start items-center`}>
            <div className={`flex items-center justify-start mt-6`}>
              <Image
                src={"/images/profileNavbar/logout.svg"}
                width={20}
                height={20}
                alt="sideBar"
                color="inherit"
                style={{ objectFit: "contain" }}
                quality={75}
              />
              <span className={`ml-3 text-sm font-medium`}>Log out</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
